import { FC } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { FileUpload } from 'components/Forms'
import { useUpload, useGetUrl, useAttachmentProgress } from 'components/Hooks'
import { Button, List } from 'ui'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'
import { GetUrlHandler, UploadHandler } from 'components/Hooks/attachments'
import { UserFile } from 'Api'
import { createNotification } from 'store/actions/notifications'

type Props = {
  uploadHandler: UploadHandler
  getUrlHandler: GetUrlHandler
  onAfterDrop: (file?: UserFile) => void
  onError?: () => void
  onSuccess?: () => void
  onAfterRemove: (file?: UserFile) => void
  files: UserFile[]
  _id?: string
}

export const FileAttachments: FC<Props> = ({ onAfterDrop, onAfterRemove, files, uploadHandler, getUrlHandler, onError, onSuccess, _id }) => {
  const [progress, handleProgress] = useAttachmentProgress()
  const { t } = useTranslation('legacy')

  const handleFileDrop = async ({ file, onAfterDrop }) => {
    if (!file) return
    const key = await uploadHandler({ fileName: file.name, file, _id, contentType: file.type, onProgress: handleProgress })
    if (!key) return onError?.()

    const newFile: UserFile = { key, name: file.name, type: file.type }
    onAfterDrop(newFile)
    onSuccess?.()
  }

  const handleRemove = async ({ file, onAfterRemove }) => {
    if (!file.key) return
    onAfterRemove(file)
  }

  return (
    <>
      <FileUpload onChange={file => handleFileDrop({ file, onAfterDrop })} />
      <div>
        {progress && progress.length > 0
          ? progress.map(file => (
              <div key={file.name}>
                <div>{file.name}</div>
                <ProgressBar progress={file.progress} />
              </div>
            ))
          : null}
      </div>
      <List>
        {files?.map(file => {
          if (!file) return null
          return (
            <List.Item className="list-group-item col-12 d-flex flex-row justify-content-between" key={file.key}>
              <Button
                type="button"
                isLink
                onClick={async () => {
                  const url = await getUrlHandler({ key: file.key })
                  if (url) window.open(url, '_blank')
                }}
              >
                <div className="m-2 d-flex flex-row align-items-center">
                  <div>
                    <div className="fa-regular fa-file" />
                  </div>
                  <div className="ml-2 text-break" data-testid="file-name">
                    {file.name}
                  </div>
                </div>
              </Button>
              <div className="text-center m-2">
                <Button type="button" data-testid="remove" onClick={() => handleRemove({ onAfterRemove, file })} color="error">
                  {t('DELETE')}
                </Button>
              </div>
            </List.Item>
          )
        })}
      </List>
    </>
  )
}

type FileAttachmentsContainerProps = Omit<Props, 'uploadHandler' | 'getUrlHandler'> & {
  attachmentType: ValueOf<typeof UPLOAD_ATTACHMENTS_TYPES>
}

const FileAttachmentsContainer: FC<FileAttachmentsContainerProps> = ({ files, onAfterDrop, onAfterRemove, attachmentType, _id }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('legacy')
  const { uploadHandler } = useUpload({ attachmentType })
  const { getUrlHandler } = useGetUrl({ attachmentType })

  const onError = () => {
    dispatch(createNotification({ title: t('upload.UPLOAD_FAILED.TITLE'), description: t('UPLOAD_FAILED.DESCRIPTION'), type: 'danger' }))
  }

  const onSuccess = () => {
    dispatch(createNotification({ title: t('upload.UPLOAD_SUCCESS.TITLE'), description: t('upload.UPLOAD_SUCCESS.DESCRIPTION'), type: 'success' }))
  }

  return (
    <FileAttachments
      onError={onError}
      onSuccess={onSuccess}
      uploadHandler={uploadHandler}
      files={files}
      onAfterDrop={onAfterDrop}
      onAfterRemove={onAfterRemove}
      getUrlHandler={getUrlHandler}
      _id={_id}
    />
  )
}

const ProgressBar: FC<{ progress: number }> = ({ progress }) => {
  return (
    <div className="my-2 progress w-full">
      <div
        className="progress-bar bg-success"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  )
}

export default FileAttachmentsContainer
